import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect, useMemo } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell, Dialog, MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import CommitButton from "../../../../../../component/CommitButton";
import EmptyState from "../../../../../../component/EmptyState";
import { StatCard, StatusChip, headCellSx, bodyCellSx, selectSx } from "../../../../../../component/listing";
import { getPayrollRuns, createPayrollRun } from "../../../../../../services/payroll";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { MONTHS, money, periodLabel, RUN_STATUS_TONE, RUN_STATUS_LABEL } from "../payrollUtils";

const now = new Date();

const PayrollRunDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [creating, setCreating] = useState(false);

  const fetchRuns = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getPayrollRuns();
      if (res?.success === false) throw new Error(res?.message || "Failed to load payroll runs");
      setRuns(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setError(true);
      notify.error(e?.message || "Failed to load payroll runs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRuns(); /* eslint-disable-next-line */ }, []);

  const stats = useMemo(() => {
    const approved = runs.filter((r) => r.status === "approved" || r.status === "paid");
    const lastNet = approved[0]?.totals?.totalNet || 0;
    return {
      total: runs.length,
      pending: runs.filter((r) => r.status === "pending_approval").length,
      lastNet,
    };
  }, [runs]);

  const runPayroll = async () => {
    setCreating(true);
    try {
      const res = await createPayrollRun({ month, year });
      if (res?.success === false) throw apiError(res, "Could not run payroll");
      notify.success(`Payroll run created for ${periodLabel(month, year)}`);
      setDialogOpen(false);
      navigate(`/app/settings/payroll/run/${res.data._id}`);
    } catch (e) {
      notify.error(e?.message || "Could not run payroll");
    } finally {
      setCreating(false);
    }
  };

  const years = [now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1];

  return (
    <Box sx={{ width: "100%" }}>
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
          <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>Payroll Runs</Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary to="/app/settings/payroll/run/off-cycle/new" sx={{ borderRadius: "8px" }}>Off-cycle</BtnSecondary>
            <BtnPrimary onClick={() => setDialogOpen(true)} sx={{ borderRadius: "8px" }}>+ Run Payroll</BtnPrimary>
          </Stack>
        </Stack>

        {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Period</TableCell>
                    <TableCell sx={headCellSx}>Employees</TableCell>
                    <TableCell align="right" sx={headCellSx}>Gross</TableCell>
                    <TableCell align="right" sx={headCellSx}>Net</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
            <TableBody><TableSkeleton columns={5} /></TableBody>
          </Table>
        ) : error ? (
          <EmptyState variant="error" title="Couldn't load payroll runs" onRetry={fetchRuns} />
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: "1.5rem" }}>
              <StatCard label="Total runs" value={stats.total} />
              <StatCard label="Pending approval" value={stats.pending} accent={tokens.warning} />
              <StatCard label="Last net payout" value={money(stats.lastNet)} accent={tokens.success} />
            </Stack>

            {runs.length === 0 ? (
              <EmptyState title="No payroll runs yet" description="Run payroll for a period to generate payslips." />
            ) : (
              <Table sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Period</TableCell>
                    <TableCell sx={headCellSx}>Employees</TableCell>
                    <TableCell align="right" sx={headCellSx}>Gross</TableCell>
                    <TableCell align="right" sx={headCellSx}>Net</TableCell>
                    <TableCell sx={headCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow key={r._id} hover onClick={() => navigate(`/app/settings/payroll/run/${r._id}`)}
                      sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                        {periodLabel(r.month, r.year)}
                        {r.runType === "off_cycle" && <Box component="span" sx={{ ml: 1, fontSize: "0.8125rem", fontWeight: 600, color: tokens.accent, background: `${tokens.accentTint}`, borderRadius: "6px", px: 1, py: 0.5 }}>Off-cycle</Box>}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{r.totals?.employeeCount || 0}</TableCell>
                      <TableCell align="right" sx={{ ...bodyCellSx, color: tokens.textMuted }}>{money(r.totals?.totalGross)}</TableCell>
                      <TableCell align="right" sx={{ ...bodyCellSx, fontWeight: 600 }}>{money(r.totals?.totalNet)}</TableCell>
                      <TableCell sx={bodyCellSx}><StatusChip label={RUN_STATUS_LABEL[r.status] || r.status} tone={RUN_STATUS_TONE[r.status]} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </MainBox>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "26rem" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Run Payroll</Box>
        <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mb: "1.5rem" }}>
          Snapshots all active salary structures for the chosen period.
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ mb: "2rem" }}>
          <Select value={month} onChange={(e) => setMonth(e.target.value)} size="small" sx={{ ...selectSx, flex: 1 }}>
            {MONTHS.map((m, i) => <MenuItem key={m} value={i}>{m}</MenuItem>)}
          </Select>
          <Select value={year} onChange={(e) => setYear(e.target.value)} size="small" sx={{ ...selectSx, width: "8rem" }}>
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
        </Stack>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BtnSecondary onClick={() => setDialogOpen(false)} disabled={creating} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          {/* One of the book's three marigold actions, and the one it calls the
              highest-stakes in the product. Marigold sits on the irreversible
              confirm, never on the button that merely opens this dialog. */}
          <CommitButton onClick={runPayroll} disabled={creating} sx={{ borderRadius: "8px" }}>{creating ? "Running…" : "Run Payroll"}</CommitButton>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default PayrollRunDashboard;
